import { useState, useEffect } from "react"
import { Loader2, Trophy, Medal } from "lucide-react"
import SwimmerSidebar from "../components/SwimmerSidebar"
import EmptyState from "../components/EmptyState"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function SwimmerResults() {
  const [results, setResults] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const { t } = useLanguage()

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)
      setError("")
      try {
        const res = await fetch(`${API}/api/me/my-results`, { headers: authHeaders() })
        if (!res.ok) throw new Error("Failed to fetch results")
        setResults(await res.json())
      } catch (e) {
        setError(e.message)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <SwimmerSidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="px-4 pt-6 pb-0 sm:px-6 sm:pt-8 lg:px-10 lg:pt-10">
          <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("results_title")}</h1>
          <p className="mt-1 text-sm text-mist-300">{t("results_subtitle")}</p>
        </div>

        <div className="px-4 py-6 sm:px-6 lg:px-10 lg:py-8 mt-4">
          {error && (
            <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-gold-400" />
            </div>
          ) : (
            <ResultsList results={results} t={t} />
          )}
        </div>
      </main>
    </div>
  )
}

/* ── Results List ── */
function ResultsList({ results, t }) {
  if (results.length === 0) {
    return (
      <EmptyState
        icon={<Trophy className="h-12 w-12 text-mist-600" />}
        title={t("results_empty")}
        hint={t("results_empty_hint")}
      />
    )
  }

  return (
    <div className="space-y-6">
      {results.map((r, i) => (
        <div key={i} className="rounded-xl border border-white/10 bg-ink-800/30 overflow-hidden">
          {/* Result header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/5"
            style={{ background: "rgba(255,255,255,0.02)" }}>
            <div>
              <h3 className="font-display text-lg font-bold text-paper-0">{r.event_title}</h3>
              <p className="text-xs text-mist-500 mt-0.5 uppercase tracking-widest font-semibold">{r.category}</p>
            </div>
            <RankBadge rank={r.my_rank} />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 divide-x divide-white/5 px-0">
            <StatBox label={t("results_my_rank")} value={`${r.my_rank} / ${r.field_size}`} accent />
            <StatBox label={t("results_my_time")} value={r.my_time} />
            <StatBox label={t("results_field_size")} value={`${r.field_size} ${t("results_swimmers")}`} small />
          </div>

          {/* Podium */}
          <div className="px-6 py-5">
            <h4 className="text-xs font-bold uppercase tracking-wider text-mist-500 mb-4 flex items-center gap-2">
              <Medal className="h-3.5 w-3.5" /> {t("results_top5")}
            </h4>
            <div className="divide-y divide-white/5">
              {r.podium.map((p) => (
                <div
                  key={p.id}
                  className={`flex items-center justify-between py-2.5 px-3 rounded-lg transition-colors ${p.rank === r.my_rank ? "bg-gold-500/5 border border-gold-500/10" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 text-center shrink-0">
                      {p.rank === 1 && <Medal className="h-4 w-4 text-yellow-400 mx-auto" />}
                      {p.rank === 2 && <Medal className="h-4 w-4 text-slate-300 mx-auto" />}
                      {p.rank === 3 && <Medal className="h-4 w-4 text-amber-600 mx-auto" />}
                      {p.rank > 3 && <span className="text-xs font-bold text-mist-500">{p.rank}</span>}
                    </div>
                    <span className={`text-sm ${p.rank === r.my_rank ? "font-bold text-gold-400" : "font-medium text-paper-0"}`}>
                      {p.swimmer_name}
                      {p.rank === r.my_rank && <span className="ml-2 text-[9px] rounded-full bg-gold-500/20 px-2 py-0.5 text-gold-400 font-bold uppercase">{t("results_you")}</span>}
                    </span>
                  </div>
                  <span className="text-sm font-mono text-mist-300">{p.finish_time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function StatBox({ label, value, accent, small }) {
  return (
    <div className="px-6 py-4 text-center">
      <p className="text-[10px] font-bold uppercase tracking-wider text-mist-500 mb-1">{label}</p>
      <p className={`font-display font-bold ${accent ? "text-2xl text-gold-400" : small ? "text-sm text-mist-300" : "text-2xl text-paper-0"}`}>{value}</p>
    </div>
  )
}

function RankBadge({ rank }) {
  if (!rank) return null
  const color = rank === 1 ? "#FFD700" : rank === 2 ? "#C0C0C0" : rank === 3 ? "#CD7F32" : null
  return (
    <div className="flex items-center gap-2 rounded-xl px-4 py-2 border"
      style={{
        background: color ? `${color}15` : "rgba(255,255,255,0.05)",
        borderColor: color ? `${color}30` : "rgba(255,255,255,0.08)"
      }}>
      {rank <= 3 && <Medal className="h-4 w-4" style={{ color: color || "#D9B36C" }} />}
      <span className="text-sm font-bold" style={{ color: color || "#D9B36C" }}>#{rank}</span>
    </div>
  )
}
